import { useCallback, useMemo, useState } from "react";

import { filter, keys, map, startCase, uniqBy, values } from "lodash";

import { Close, FilterAlt } from "@mui/icons-material";
import { Box, Collapse, Grid, lighten, Stack, Typography } from "@mui/material";

import {
  AutocompleteStaffSelector,
  CustomButton,
  CustomSelect,
  ShiftTypeFilterDropdown,
} from "~/common/components";
import { EShiftTypeDropdownHolder } from "~/routes/api/types";

import { initialState } from "#/features/SchedulerGrid/store/initialState";
import { useAppConfigQuery } from "#/features/User/queries";
import {
  EventLog,
  IShiftType,
  IStaffDetails,
  IUser,
  Schedule,
  ShiftType,
  useListScheduleShiftTypeQuery,
  useListStaffDetailsQuery,
  useListUsersQuery,
  User,
} from "@/api";
import { TChangeReportSidebarFilters } from "@/common/components/StaffShiftHistoryModal/types";
import { NOT_EXISTING_UUID } from "@/common/constants";
import { useCurrentSelectedUnitId, useCurrentTimezone, useKeyBy } from "@/common/hooks";
// eslint-disable-next-line deprecate/import
import { localDayJs } from "@/common/packages/dayjs";
import { black, darkPurple, mediumGray, white } from "@/common/theming/colors";

import CustomDatePicker from "../DeprecatedDatePicker";

const emptyStaffDetails: IStaffDetails[] = [];
const emptyUsers: IUser[] = [];
const emptyShiftTypes: ShiftType.DTO[] = [];
const eventTypeOptions = keys(EventLog.EEventType)
  .filter(
    (event) =>
      EventLog.EEventType[event] !== EventLog.EEventType.userCreated &&
      EventLog.EEventType[event] !== EventLog.EEventType.userUpdated,
  )
  .map((eventType) => ({
    value: EventLog.EEventType[eventType],
    label: startCase(EventLog.EEventType[eventType]),
  }));

export const Filters = ({
  filters,
  setFilters,
  availableSchedules,
}: {
  filters: TChangeReportSidebarFilters;
  setFilters: (filtersUpdated: Partial<TChangeReportSidebarFilters | undefined>) => void;
  availableSchedules: Schedule.DTO[];
}) => {
  const currentUnitId = useCurrentSelectedUnitId();
  const timezone = useCurrentTimezone(currentUnitId);

  const { data: staffDetails = emptyStaffDetails } = useListStaffDetailsQuery(
    { homeUnitIds: [currentUnitId || NOT_EXISTING_UUID] },
    { skip: !currentUnitId },
  );
  const { data: users = emptyUsers } = useListUsersQuery({
    unitIds: [currentUnitId || NOT_EXISTING_UUID],
  });

  const { data: availableShiftTypes = emptyShiftTypes } = useListScheduleShiftTypeQuery(
    {
      scheduleIds: filters.scheduleIds.length ? filters.scheduleIds : map(availableSchedules, "id"),
    },
    { skip: !filters.scheduleIds || !availableSchedules.length },
  );

  const adminUsers = useAppConfigQuery().data?.admins || emptyUsers;

  const changeMadeByUserSelectOptions = useMemo(() => {
    return [...users, ...(adminUsers as User.DTO[])];
  }, [users, adminUsers]);

  const scheduleSelectorItems = useMemo(() => {
    if (!availableSchedules) return [];
    return availableSchedules?.map(({ endDay, startDay, id }) => ({
      value: id,
      label: `${localDayJs(startDay).format("MM-DD-YYYY")} - ${localDayJs(endDay).format(
        "MM-DD-YYYY",
      )}`,
    }));
  }, [availableSchedules]);

  const shiftTypesByKey = useKeyBy(uniqBy(availableShiftTypes, "key"), "key");

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const filtersSelected = useMemo(() => {
    return (
      !!filters.scheduleIds.length ||
      !!filters.staffIds.length ||
      !!filters.eventTypes.length ||
      !!filters.authorIds.length ||
      !!filters.dateOfChange ||
      !!filters.dateImpacted ||
      !!filters.shiftTypeKeys.length
    );
  }, [filters]);

  const toggleFiltersCollapse = useCallback(
    (event: React.SyntheticEvent, forceOpenOrClose?: boolean) => {
      if (event.defaultPrevented) return;
      event.stopPropagation();
      setModalIsOpen(forceOpenOrClose ?? !modalIsOpen);
    },
    [modalIsOpen],
  );

  return (
    <Box
      className="filters-container"
      sx={{
        border: modalIsOpen ? `2px solid ${black}` : `1px solid ${mediumGray}`,
        borderRadius: "4px",
        ".filters-section .MuiChip-root": { height: "32px" },
        ".filters-section  .MuiChip-label": { px: "12px" },
        ".filters-actions": { alignSelf: "flex-start" },
      }}
      onClick={toggleFiltersCollapse}
    >
      <Stack direction={"row"} alignItems={"center"} p={1}>
        <CustomButton
          label="Filters"
          sx={{
            width: "80px",
            height: "25px",
            ".MuiButton-startIcon": { mr: 0 },
            mr: 1,
          }}
          startIcon={<FilterAlt />}
          variant="text"
          onClick={toggleFiltersCollapse}
        />

        <Stack direction={"row"} gap={"4px"} alignItems={"center"} flexWrap={"wrap"}>
          {!!filters.scheduleIds.length && (
            <FilterTag content={`Schedules(${filters.scheduleIds.length})`} />
          )}
          {!!filters.staffIds.length && <FilterTag content={`Staff(${filters.staffIds.length})`} />}
          {!!filters.eventTypes.length && (
            <FilterTag content={`Event Types(${filters.eventTypes.length})`} />
          )}
          {!!filters.authorIds.length && (
            <FilterTag content={`Changed By(${filters.authorIds.length})`} />
          )}
          {!!filters.dateOfChange && <FilterTag content={`Date of Change(1)`} />}
          {!!filters.dateImpacted && <FilterTag content={`Date Impacted(1)`} />}
          {!!filters.shiftTypeKeys.length && (
            <FilterTag content={`Shift Types(${filters.shiftTypeKeys.length})`} />
          )}
        </Stack>
        <Box flexGrow={1} />

        {filtersSelected && (
          <CustomButton
            label="Reset"
            sx={{
              marginRight: "2px",
              height: "25px",
              ".MuiButton-startIcon": { mr: 0 },
            }}
            variant="text"
            onClick={(event) => {
              event.preventDefault();
              setFilters({
                ...initialState.grid.sidebar.changeReportFilters,
              });
            }}
          />
        )}

        {modalIsOpen && (
          <CustomButton
            trackingLabel="Close"
            size="small"
            variant="outlined"
            sx={{
              border: `1px solid ${mediumGray}`,
              width: "32px",
            }}
            iconOnly
            startIcon={<Close sx={{ fontSize: "20px" }} />}
            onClick={(event) => toggleFiltersCollapse(event, false)}
          />
        )}
      </Stack>

      <Collapse
        in={modalIsOpen}
        sx={{
          zIndex: 10,
          overflowY: "scroll",
          background: white,
          position: "relative",
          ".MuiCheckbox-root": {
            padding: "4px !important",
          },
          maxHeight: "300px",
        }}
        // Prevents closing the collapse
        onClick={(event) => event.stopPropagation()}
      >
        <Grid className="filters-section" container gap={1.5} sx={{ padding: 2 }}>
          <Grid
            item
            width="100%"
            sx={{
              ".MuiFormLabel-root[data-shrink='false']": {
                transform: "translate(14px, 12px) !important",
              },
            }}
          >
            <CustomSelect
              trackingLabel="schedule-period-dropdown"
              items={scheduleSelectorItems.map((scheduleSelectItem) => {
                return scheduleSelectItem;
              })}
              multiple
              label="Schedule Period"
              value={filters.scheduleIds}
              onChange={(event) => {
                const scheduleIds = Array.isArray(event.target.value)
                  ? event.target.value
                  : ([event.target.value] as Schedule.DTO["id"][]);
                setFilters({ scheduleIds });
              }}
              noValueText="There are no schedules to select"
              sx={{ minHeight: "49px" }}
            />
          </Grid>
          <Grid item width="100%">
            <AutocompleteStaffSelector
              staffOptions={staffDetails}
              selectedStaffIds={filters.staffIds}
              setSelectedStaffIds={(staffIds) => setFilters({ staffIds })}
              label="Staff Name"
              trackingLabel="staff-name-dropdown"
              sx={{
                minHeight: "49px",
                ".MuiOutlinedInput-root": {
                  padding: "5.5px",
                },
                ".MuiFormLabel-root[data-shrink='false']": {
                  transform: "translate(14px, 12px) !important",
                },
              }}
            />
          </Grid>

          <Grid
            item
            width="100%"
            sx={{
              ".MuiFormLabel-root[data-shrink='false']": {
                transform: "translate(14px, 12px) !important",
              },
            }}
          >
            <ShiftTypeFilterDropdown
              shiftTypes={values(shiftTypesByKey)}
              dropdownTypeOptionsToInclude={EShiftTypeDropdownHolder.schedulerGrid}
              selectedOption={filter(
                filters.shiftTypeKeys.map((shiftTypeKey) => shiftTypesByKey[shiftTypeKey]),
              )}
              selectOption={(newSelection) => {
                const newShiftTypes = (newSelection as IShiftType[]) || [];
                setFilters({ shiftTypeKeys: newShiftTypes.map(({ key }) => key) });
              }}
              isMultiSelect
              label="Shift Type"
              sx={{
                minHeight: "49px",
              }}
              trackingLabel="shift-type-dropdown"
            />
          </Grid>

          <Grid
            item
            width="100%"
            sx={{
              ".MuiFormLabel-root[data-shrink='false']": {
                transform: "translate(14px, 12px) !important",
              },
            }}
          >
            <CustomSelect
              items={eventTypeOptions}
              label="Event Type"
              placeholder="Event Type"
              trackingLabel="event-type-dropdown"
              multiple
              value={filters.eventTypes}
              onChange={(event) => {
                const eventTypes = Array.isArray(event.target.value)
                  ? event.target.value
                  : ([event.target.value] as EventLog.EEventType[]);
                setFilters({ eventTypes });
              }}
              sx={{
                minHeight: "49px",
                label: {
                  backgroundColor: "red!important",
                },
              }}
            />
          </Grid>
          <Grid item width="100%">
            <AutocompleteStaffSelector
              staffOptions={changeMadeByUserSelectOptions}
              selectedStaffIds={filters.authorIds}
              setSelectedStaffIds={(authorIds) => setFilters({ authorIds })}
              label="Change Made By"
              trackingLabel="change-made-by-dropdown"
              sx={{
                ".MuiOutlinedInput-root": {
                  padding: "5.5px",
                },
                ".MuiFormLabel-root[data-shrink='false']": {
                  transform: "translate(14px, 12px) !important",
                },
              }}
            />
          </Grid>
          <Grid
            item
            width="100%"
            sx={{
              ".MuiFormLabel-root[data-shrink='false']": {
                transform: "translate(14px, 12px) !important",
              },
            }}
          >
            <CustomDatePicker
              name="dateOfChange"
              label="Date of Change"
              clearable
              value={filters.dateOfChange}
              onChange={(newDate) => {
                setFilters({
                  dateOfChange: newDate,
                });
              }}
              timezone={timezone}
              sx={{ ".MuiOutlinedInput-root": { minHeight: "49px" } }}
            />
          </Grid>
          <Grid
            item
            width="100%"
            sx={{
              ".MuiFormLabel-root[data-shrink='false']": {
                transform: "translate(14px, 12px) !important",
              },
            }}
          >
            <CustomDatePicker
              name="dateImpacted"
              label="Date Impacted"
              value={filters.dateImpacted}
              clearable
              onChange={(newDate) => {
                setFilters({
                  dateImpacted: newDate,
                });
              }}
              timezone={timezone}
              sx={{ ".MuiOutlinedInput-root": { minHeight: "49px" } }}
            />
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
};

const FilterTag = ({ content }: { content: string }) => (
  <Typography
    sx={{
      color: darkPurple,
      background: lighten(darkPurple, 0.9),
      textWrap: "nowrap",
      fontWeight: "500",
      padding: "2px 4px",
      fontSize: "11px",
      borderRadius: "4px",
      border: `1px solid ${darkPurple}`,
    }}
    children={content}
  />
);
